import { Injectable } from '@angular/core';
import { PeopleSearchType } from '../constants-types/Enums';
import { FilterInfo, FilterType } from '../core-data-model/FilterInfo.model';
import { JobFromIndex } from '../core-data-model/Job.model';
import { Job } from '../core-data-model/Job.model';
import { Person } from '../core-data-model/Person.model';
import { PeopleAndJobsSearchResult } from '../core-data-model/SearchApi.serverinterface';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { SearchState } from '../core-data-model/SearchState.model';
import { AsyncFolderDecorator } from '../core-services/Folders.dataservice';
import { ObjectCacherService } from '../core-services/ObjectCacher.service';
import { PeopleAndJobsQueryGeneratorService } from '../core-services/PeopleAndJobsQueryGenerator.service';
import { SearchTransformers } from '../core-services/SearchApi.datatransform';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';

@Injectable({
    providedIn: 'root'
})
export class SearchApi {
    constructor(
        private api: ApiService,
        private searchTransformers: SearchTransformers,
        private queryGeneratorService: PeopleAndJobsQueryGeneratorService,
        private userProfile: UserProfileSettingsService,
        private objectCache: ObjectCacherService
    ) {}

    getResults(
        searchText: string,
        filters?: FilterInfo[],
        resultCount = 5,
        searchType?: PeopleSearchType,
        companyId = this.userProfile.currentCompany
    ) {
        const oDataQuery = this.queryGeneratorService.createSearch(
            searchText,
            resultCount,
            filters,
            searchType
        );
        return this.api
            .get<PeopleAndJobsSearchResult>(
                `/api/v2/companies/${companyId}/peopleandjobs?${oDataQuery}`
            )
            .then(([results]) => {
                if (results.peopleAndJobsSearchResult === null) {
                    return [];
                }
                const myResults = results.peopleAndJobsSearchResult.map(
                    (entry) =>
                        this.searchTransformers.transformPeopleAndJobs(entry)
                );
                myResults.forEach((result) =>
                    this.objectCache.put(result.id, result)
                );
                return myResults;
            });
    }

    // TODO - this should become a call to getResults
    getPositions(
        searchText: string,
        resultCount = 10,
        filters: FilterInfo[] = []
    ): Promise<Job[]> {
        const updatedFilters = filters.concat(
            new FilterInfo({ type: FilterType.Jobs })
        );

        return this.getResults(
            searchText,
            updatedFilters,
            resultCount,
            PeopleSearchType.SortName
        ).then((results) =>
            results.filter((r): r is Job => r instanceof JobFromIndex)
        );
    }

    // TODO - this should become a call to getResults
    @AsyncFolderDecorator.waitForFolders()
    getPersons(
        searchText: string,
        filters?: FilterInfo[],
        resultCount = 5,
        searchType?: PeopleSearchType
    ): Promise<Person[]> {
        const updatedFilters: FilterInfo[] = filters.concat(
            new FilterInfo({ type: FilterType.People })
        );

        return this.getResults(
            searchText,
            updatedFilters,
            resultCount,
            searchType
        ).then((results) =>
            results.filter((r): r is Person => r instanceof Person)
        );
    }

    @AsyncFolderDecorator.waitForFolders()
    searchPeopleAndJobs(
        pristineSearchState: SearchState,
        companyId = this.userProfile.currentCompany
    ): Promise<SearchResult[]> {
        return this.api.multiplexSearchRequest(
            pristineSearchState,
            (searchState) => {
                // TODO handle the separating & and ? well
                const oDataQuery =
                    this.queryGeneratorService.createODataString(searchState);
                return this.api
                    .get<PeopleAndJobsSearchResult>(
                        `/api/v2/companies/${companyId}/peopleandjobs?${oDataQuery}`
                    )
                    .then(([results]) => {
                        pristineSearchState.pageState.maxResults =
                            results.totalSearchResults;

                        if (results.peopleAndJobsSearchResult === null) {
                            return [];
                        }
                        const searchResults =
                            results.peopleAndJobsSearchResult.map(
                                (personOrJob) =>
                                    this.searchTransformers.transformPeopleAndJobs(
                                        personOrJob
                                    )
                            );
                        searchResults.forEach((result) => {
                            if (result instanceof Person) {
                                this.objectCache.put(result.id, result);
                            }
                            if (result instanceof Job) {
                                this.objectCache.put(result.id, result);
                            }
                        });
                        return searchResults;
                    });
            }
        );
    }

    searchPeopleAndJobsWithJobHealth(
        pristineSearchState: SearchState,
        companyId = this.userProfile.currentCompany
    ): Promise<SearchResult[]> {
        return this.api.multiplexSearchRequest(
            pristineSearchState,
            (searchState) => {
                const oDataQuery =
                    this.queryGeneratorService.createODataString(searchState);
                return this.api
                    .get<PeopleAndJobsSearchResult>(
                        `/api/v2/companies/${companyId}/peopleandjobs?${oDataQuery}&addAssociatedPeopleInfo=true`
                    )
                    .then(([results]) => {
                        pristineSearchState.pageState.maxResults =
                            results.totalSearchResults;

                        if (results.peopleAndJobsSearchResult === null) {
                            return [];
                        }
                        const searchResults =
                            results.peopleAndJobsSearchResult.map(
                                (personOrJob) =>
                                    this.searchTransformers.transformPeopleAndJobs(
                                        personOrJob
                                    )
                            );
                        searchResults.forEach((result) => {
                            if (result instanceof Person) {
                                this.objectCache.put(result.id, result);
                            }
                            if (result instanceof Job) {
                                this.objectCache.put(result.id, result);
                            }
                        });
                        return searchResults;
                    });
            }
        );
    }

    searchJobsApiWithJobHealth(
        pristineSearchState: SearchState,
        companyId = this.userProfile.currentCompany
    ): Promise<SearchResult[]> {
        return this.api.multiplexSearchRequest(
            pristineSearchState,
            (searchState) => {
                const oDataQuery =
                    this.queryGeneratorService.createODataString(searchState);
                return this.api
                    .get<PeopleAndJobsSearchResult>(
                        `/api/v3/companies/${companyId}/jobs/recentlymodifiedjobsaggregate?${oDataQuery}&addAssociatedPeopleInfo=true`
                    )
                    .then(([results]) => {
                        pristineSearchState.pageState.maxResults =
                            results.totalSearchResults;

                        if (results.peopleAndJobsSearchResult === null) {
                            return [];
                        }
                        const searchResults =
                            results.peopleAndJobsSearchResult.map(
                                (personOrJob) =>
                                    this.searchTransformers.transformPeopleAndJobs(
                                        personOrJob
                                    )
                            );
                        searchResults.forEach((result) => {
                            if (result instanceof Person) {
                                this.objectCache.put(result.id, result);
                            }
                            if (result instanceof Job) {
                                this.objectCache.put(result.id, result);
                            }
                        });
                        return searchResults;
                    });
            }
        );
    }

    searchForMatch(
        firstName: string,
        lastName: string,
        email: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<Person> {
        const lastNameSearch =
            this.queryGeneratorService.createSingleFieldSearch(
                lastName,
                'lastName'
            );
        const emailSearch = this.queryGeneratorService.createSingleFieldSearch(
            email,
            'email'
        );
        return Promise.all([
            this.api.get<PeopleAndJobsSearchResult>(
                `/api/v2/companies/${companyId}/peopleandjobs?${lastNameSearch}`
            ),
            this.api.get<PeopleAndJobsSearchResult>(
                `/api/v2/companies/${companyId}/peopleandjobs?${emailSearch}`
            )
        ]).then(([[lastNameResultsRaw], [emailResultsRaw]]) => {
            // check for email match
            const emailMatch = emailResultsRaw.peopleAndJobsSearchResult
                .map((personOrJob) =>
                    this.searchTransformers.transformPeopleAndJobs(personOrJob)
                )
                .find(
                    (personResult: SearchResult) =>
                        personResult instanceof Person &&
                        personResult.email &&
                        email.toLowerCase() === personResult.email.toLowerCase()
                ) as Person;
            if (emailMatch) {
                return emailMatch;
            }
            // check for last name and first name match
            const nameMatchList =
                lastNameResultsRaw.peopleAndJobsSearchResult.map(
                    (personOrJob) =>
                        this.searchTransformers.transformPeopleAndJobs(
                            personOrJob
                        ) as Person
                );
            const nameMatch = nameMatchList.find(
                (personResult) =>
                    personResult instanceof Person &&
                    personResult.lastName &&
                    personResult.firstName &&
                    lastName.trim().toLowerCase() ===
                        personResult.lastName.trim().toLowerCase() &&
                    firstName.trim().toLowerCase() ===
                        personResult.firstName.trim().toLowerCase()
            );
            if (nameMatch) {
                return nameMatch;
            }
            // try for a rare last name
            if (nameMatchList.length === 1) {
                return nameMatchList[0];
            }
            return;
        });
    }
}
